import React, { createContext, useContext } from "react";

interface User {
  id: number;
  email: string;
  fullName: string;
  role: string;
  hireDate?: Date;
}

interface Props {
  children: React.ReactNode;
}

interface UserContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: Props) => {
  // Initialize state from localStorage
  const [currentUser, setCurrentUser] = React.useState<User | null>(() => {
    const savedUser = sessionStorage.getItem("currentUserData");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Wrapper for setUser that also updates localStorage
  const setUserAndStorage = (newUser: User | null) => {
    if (newUser) {
      sessionStorage.setItem("currentUserData", JSON.stringify(newUser));
    } else {
      sessionStorage.removeItem("currentUserData");
    }
    setCurrentUser(newUser);
  };

  return (
    <UserContext.Provider
      value={{ currentUser, setCurrentUser: setUserAndStorage }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useCurrentUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useCurrentUser must be used within a UserProvider");
  }
  return context;
};
